import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  currentLanguage: 'en' | 'zh';
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  currentLanguage,
  title,
  description,
  keywords,
  canonicalUrl = 'https://alpha-pilot.net'
}) => {
  const baseTitle = 'Alphora AI';
  const baseDescription = 'Alphora AI - Advanced AI trading analytics platform built by Citadel quant researchers. Real-time AI research, pattern recognition, strategy optimization for professional and retail traders.';
  const baseKeywords = 'AI trading,quantitative trading,trading analytics,pattern recognition,strategy optimization,cryptocurrency trading,stock analysis,trading bot,AI quant,Alphora AI,algorithmic trading,market analysis';

  const finalTitle = title || baseTitle;
  const finalDescription = description || baseDescription;
  const finalKeywords = keywords || baseKeywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="language" content="en-US" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Favicon */}
      <link rel="icon" href="/logo.png" />
      <link rel="apple-touch-icon" href="/logo.png" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={`${canonicalUrl}/og-image.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="AlphoraAI" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={finalTitle} />
      <meta property="twitter:description" content={finalDescription} />
      <meta property="twitter:image" content={`${canonicalUrl}/twitter-image.jpg`} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="AlphoraAI" />
      <meta name="application-name" content="AlphoraAI" />
      <meta name="apple-mobile-web-app-title" content="AlphoraAI" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "AlphoraAI",
          "description": finalDescription,
          "url": canonicalUrl,
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          },
          "creator": {
            "@type": "Organization",
            "name": "AlphoraAI",
            "description": "Built by Citadel quant researchers and AI engineers"
          },
          "featureList": [
            "AI-powered research",
            "Pattern recognition", 
            "Strategy optimization",
            "Real-time market insights"
          ],
          "screenshot": `${canonicalUrl}/screenshot.jpg`,
          "inLanguage": "en-US"
        })}
      </script>
    </Helmet>
  );
};
